import { memoize } from "lodash";
import { MetadataAsset, SortField, Video } from "./types";

export function* chunks<T>(arr: T[], n: number): Generator<T[], void> {
  for (let i = 0; i < arr.length; i += n) {
    yield arr.slice(i, i + n);
  }
}

const colors = [
  "cyan",
  "magenta",
  "geekblue",
  "purple",
  "blue",
  "yellow",
  "pink",
  "gold",
  "volcano",
];

const videoColors: Record<string, string> = {};
export const getVideoTypeColor = memoize((videoType: string | undefined) => {
  if (!videoType) {
    return "blue";
  }
  if (videoColors[videoType]) {
    return videoColors[videoType];
  } else {
    const nextColor =
      colors.find((color) => !Object.values(videoColors).includes(color)) ??
      "blue";
    videoColors[videoType] = nextColor;
    return nextColor;
  }
});

export const getVideoTypeLabel = (videoType: string | undefined) => {
  if (videoType && videoType.indexOf("_") > 0) {
    // remove prefix
    return videoType.split("_")[1];
  } else {
    return videoType;
  }
};

const searchInField = (value: string, query: string) =>
  value.toLocaleLowerCase().includes(query.toLocaleLowerCase());

export const filterVideos = (
  items: Video[],
  search: string,
  filterValue: string[]
) => {
  const typeFilter = filterValue
    .filter((x) => x.startsWith("type:"))
    .map((x) => x.split(":")[1]);
  const marketFilter = filterValue
    .filter((x) => x.startsWith("market:"))
    .map((x) => x.split(":")[1]);
  const statusFilter = filterValue
    .filter((x) => x.startsWith("status:"))
    .map((x) => x.split(":")[1]);

  const keywords = search
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean); // remove trailing space

  return items.filter((video) => {
    const metadataAssets = video.metadata.assets as MetadataAsset[] | undefined;

    const typeMatches =
      typeFilter.length === 0 || typeFilter.includes(video.metadata.type);
    if (!typeMatches) return false;

    const marketMatches =
      marketFilter.length === 0 ||
      marketFilter.includes(metadataAssets?.[0]?.market as string);
    if (!marketMatches) return false;

    const statusMatches =
      statusFilter.length === 0 ||
      (video.status && statusFilter.includes(video.status));

    if (!statusMatches) return false;

    if (keywords.length === 0) return true;
    const assetMatches = keywords.some((keyword) =>
      metadataAssets?.some(
        (asset) =>
          searchInField(asset.name as string, keyword) ||
          searchInField(asset.ticker as string, keyword) ||
          searchInField(asset.market as string, keyword)
      )
    );

    return assetMatches;
  });
};

export const sortVideos = (items: Video[], sortField: SortField) => {
  if (sortField === "type") {
    return items.sort((a, b) =>
      (a.metadata.type ?? "").localeCompare(b.metadata.type ?? "")
    );
  }
  if (sortField === "status") {
    return items.sort((a, b) => (a.status ?? "").localeCompare(b.status ?? ""));
  }
  return items.sort((a, b) => {
    const aAssets = a.metadata.assets as MetadataAsset[] | undefined;
    const bAssets = b.metadata.assets as MetadataAsset[] | undefined;
    return String(aAssets?.[0]?.[sortField] ?? "").localeCompare(
      String(bAssets?.[0]?.[sortField] ?? "")
    );
  });
};
